import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../services/api';
import { useSearchParams } from 'react-router-dom';
import GoogleAuthButton from './GoogleAuthButton';
import ForgotPasswordForm from './ForgotPasswordForm';

const Login = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const [isSignup, setIsSignup] = useState(searchParams.get('signup') === 'true');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [formData, setFormData] = useState({ username: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const authUrl = process.env.REACT_APP_AUTH_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (isSignup) {
        if (!formData.email) {
          setError('Email is required for registration');
          setLoading(false);
          return;
        }
        const response = await authAPI.register(formData.username, formData.email, formData.password);
        if (response.user) onLogin(response.user);
      } else {
        const response = await authAPI.login(formData.username, formData.password);
        if (response.user) onLogin(response.user);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.email) { setError('Please enter your email address'); return; }
    setLoading(true);
    try {
      await authAPI.requestPasswordReset(formData.email);
      setSuccess('If an account exists with this email, you will receive a password reset link shortly.');
      setFormData({ ...formData, email: '' });
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${authUrl}/google?redirect_uri=${encodeURIComponent(window.location.origin)}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (sessionId) {
      authAPI.processGoogleSession(sessionId)
        .then(response => {
          if (response.user) {
            window.history.replaceState({}, document.title, window.location.pathname);
            onLogin(response.user);
          }
        })
        .catch(() => setError('Failed to complete Google sign-in'));
    }
  }, [onLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-2xl">
          <CardHeader className="space-y-2 text-center pb-6">
            <div className="mx-auto w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-2">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <CardTitle className="text-2xl sm:text-3xl font-bold">TaskPriority Prime</CardTitle>
            <CardDescription className="text-base">
              {isForgotPassword ? 'Reset your password' : (isSignup ? 'Create your account' : 'Welcome back')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isForgotPassword ? (
              <ForgotPasswordForm
                email={formData.email}
                onChange={handleChange}
                onSubmit={handleForgotPasswordRequest}
                onBack={() => { setIsForgotPassword(false); setError(''); setSuccess(''); setFormData({ ...formData, email: '' }); }}
                loading={loading}
                error={error}
                success={success}
              />
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username or Email</Label>
                  <Input id="username" name="username" type="text" placeholder="Enter your username or email" value={formData.username} onChange={handleChange} required className="h-11" data-testid="username-input" />
                </div>

                {isSignup && (
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" name="email" type="email" placeholder="Enter your email" value={formData.email} onChange={handleChange} required className="h-11" />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={formData.password} onChange={handleChange} required className="h-11 pr-10" data-testid="password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="toggle-password-visibility" tabIndex={-1}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
                )}
                {success && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-lg text-sm">{success}</div>
                )}

                <Button type="submit" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium" disabled={loading} data-testid="login-submit-btn">
                  {loading ? 'Please wait...' : (isSignup ? 'Sign Up' : 'Sign In')}
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-300"></div></div>
                  <div className="relative flex justify-center text-sm"><span className="px-4 bg-white dark:bg-gray-950 text-slate-500">Or continue with</span></div>
                </div>

                <GoogleAuthButton onClick={handleGoogleLogin} />

                <div className="text-center space-y-2">
                  <Button type="button" variant="link" onClick={() => { setIsSignup(!isSignup); setError(''); setSuccess(''); }} className="text-base font-semibold text-blue-600 hover:text-blue-700">
                    {isSignup ? 'Already have an account? Sign in' : (<>Don&apos;t have an account? <span className="underline">Sign up now!</span></>)}
                  </Button>
                  {!isSignup && (
                    <Button type="button" variant="link" onClick={() => { setIsForgotPassword(true); setError(''); setSuccess(''); setFormData({ ...formData, email: '' }); }} className="text-sm text-slate-600 hover:text-slate-700 block w-full">
                      Forgot password?
                    </Button>
                  )}
                </div>
              </form>
            )}
          </CardContent>
        </Card>
        <div className="text-center mt-4 space-x-4">
          <a href="/notes" className="text-sm text-slate-500 hover:text-blue-600 transition-colors" data-testid="about-notes-link">About TaskPriority Prime</a>
          <a href="/privacy" className="text-sm text-slate-500 hover:text-blue-600 transition-colors" data-testid="privacy-link">Privacy Policy</a>
          <a href="/terms" className="text-sm text-slate-500 hover:text-blue-600 transition-colors" data-testid="terms-link">Terms of Service</a>
        </div>
      </div>
    </div>
  );
};

export default Login;
